import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import { toast } from 'react-toastify';
import {
  defaultMissionReportRange,
  fetchMissionAnalyticsReport,
  formatYmd,
  type MissionAnalyticsReport,
} from '../../services/missionsAnalytics';
import LoadingScreen from 'src/Loading/LoadingScreen';

const PALETTE = ['#2563eb', '#16a34a', '#ea580c', '#9333ea', '#dc2626', '#0891b2', '#ca8a04', '#4f46e5'];

const InstitutionMissionCharts: React.FC = () => {
  const { t } = useTranslation();
  // Memoised wrapper around `t` so the function reference is stable
  // across renders. The previous version recreated `tr` on every render,
  // which made the `load` useCallback below recompute every render,
  // which made its useEffect refire every render — producing an
  // infinite loop of `/missions/reports/summary` requests (visible as
  // hundreds of duplicate calls in the Network tab).
  const tr = useCallback(
    (key: string) => String(t(`institution.${key}`)),
    [t],
  );

  const initial = useMemo(() => defaultMissionReportRange(), []);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [report, setReport] = useState<MissionAnalyticsReport | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    try {
      const result = await fetchMissionAnalyticsReport(from, to);
      if (!result.ok) {
        if (result.status === 403) {
          setForbidden(true);
          setReport(null);
          return;
        }
        toast.error(result.message);
        setReport(null);
        return;
      }
      setReport(result.data);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : tr('missions_load_error'));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, tr]);

  useEffect(() => {
    void load();
  }, [load]);

  const missionsByDayChart = useMemo(() => {
    if (!report?.missionsByDay?.length) return [];
    return report.missionsByDay.map((d) => ({
      ...d,
      label: d.date.slice(5),
    }));
  }, [report]);

  const pieMissionStatus = useMemo(() => {
    if (!report?.missionsByStatus?.length) return [];
    return report.missionsByStatus.map((x) => ({ name: x.status, value: x.count }));
  }, [report]);

  const pieCheckpointStatus = useMemo(() => {
    if (!report?.checkpointsByStatus?.length) return [];
    return report.checkpointsByStatus.map((x) => ({ name: x.status, value: x.count }));
  }, [report]);

  if (forbidden) {
    return (
      <section className="inst-missions-dash inst-missions-dash--muted" aria-label={tr('missions_section_title')}>
        <p className="inst-missions-dash__hint mb-0">{tr('missions_supervisor_only')}</p>
      </section>
    );
  }

  return (
    <section className="inst-missions-dash" aria-label={tr('missions_section_title')}>
      <div className="inst-missions-dash__head">
        <div>
          <h2 className="inst-missions-dash__title">{tr('missions_section_title')}</h2>
          <p className="inst-missions-dash__subtitle">{tr('missions_section_subtitle')}</p>
        </div>
        <div className="inst-missions-dash__filters">
          <label className="inst-missions-dash__lab">
            <span>{tr('missions_from')}</span>
            <input
              type="date"
              className="inst-missions-dash__date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="inst-missions-dash__lab">
            <span>{tr('missions_to')}</span>
            <input
              type="date"
              className="inst-missions-dash__date"
              value={to}
              min={from}
              max={formatYmd(new Date())}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <button type="button" className="inst-missions-dash__btn" onClick={() => void load()}>
            {tr('missions_apply_range')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="inst-missions-dash__loading">
          <LoadingScreen />
        </div>
      ) : !report ? (
        <p className="inst-missions-dash__hint">{tr('missions_no_data')}</p>
      ) : (
        <>
          <div className="inst-missions-dash__kpis">
            <div className="inst-missions-kpi">
              <span className="inst-missions-kpi__val">{report.totals.missions}</span>
              <span className="inst-missions-kpi__lab">{tr('missions_kpi_missions')}</span>
            </div>
            <div className="inst-missions-kpi">
              <span className="inst-missions-kpi__val">{report.totals.completedMissions}</span>
              <span className="inst-missions-kpi__lab">{tr('missions_kpi_completed')}</span>
            </div>
            <div className="inst-missions-kpi">
              <span className="inst-missions-kpi__val">
                {report.totals.checkpointsTotal
                  ? Math.round((100 * report.totals.checkpointsPassed) / report.totals.checkpointsTotal)
                  : 0}
                %
              </span>
              <span className="inst-missions-kpi__lab">{tr('missions_kpi_cp_pass_rate')}</span>
            </div>
            <div className="inst-missions-kpi">
              <span className="inst-missions-kpi__val">{report.totals.checkpointsFlagged}</span>
              <span className="inst-missions-kpi__lab">{tr('missions_kpi_flagged')}</span>
            </div>
            <div className="inst-missions-kpi">
              <span className="inst-missions-kpi__val">{report.totals.pointsEarned}</span>
              <span className="inst-missions-kpi__lab">{tr('missions_kpi_points_earned')}</span>
            </div>
            <div className="inst-missions-kpi">
              <span className="inst-missions-kpi__val">{report.totals.pointsAvailable}</span>
              <span className="inst-missions-kpi__lab">{tr('missions_kpi_points_available')}</span>
            </div>
          </div>

          <div className="inst-missions-dash__grid">
            <div className="inst-missions-card">
              <h3 className="inst-missions-card__title">{tr('missions_chart_missions_by_day')}</h3>
              <div className="inst-missions-card__chart">
                {missionsByDayChart.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={missionsByDayChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} width={32} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="count" name={tr('missions_series_count')} stroke="#2563eb" strokeWidth={2} dot />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="inst-missions-card__empty">{tr('missions_chart_empty')}</p>
                )}
              </div>
            </div>

            <div className="inst-missions-card">
              <h3 className="inst-missions-card__title">{tr('missions_chart_status')}</h3>
              <div className="inst-missions-card__chart">
                {pieMissionStatus.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={pieMissionStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={88} label>
                        {pieMissionStatus.map((_, i) => (
                          <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="inst-missions-card__empty">{tr('missions_chart_empty')}</p>
                )}
              </div>
            </div>

            <div className="inst-missions-card inst-missions-card--wide">
              <h3 className="inst-missions-card__title">{tr('missions_chart_checkpoints')}</h3>
              <div className="inst-missions-card__chart">
                {pieCheckpointStatus.length ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={report.checkpointsByStatus} margin={{ top: 8, right: 16, left: 0, bottom: 48 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="status" interval={0} angle={-28} textAnchor="end" height={56} tick={{ fontSize: 10 }} />
                      <YAxis allowDecimals={false} width={36} />
                      <Tooltip />
                      <Bar dataKey="count" name={tr('missions_series_count')} fill="#9333ea" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="inst-missions-card__empty">{tr('missions_chart_empty')}</p>
                )}
              </div>
            </div>

            <div className="inst-missions-card inst-missions-card--wide">
              <h3 className="inst-missions-card__title">{tr('missions_chart_by_sop')}</h3>
              <div className="inst-missions-card__chart">
                {report.missionsBySop.length ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={report.missionsBySop.map((s) => ({
                        name: `${s.sopCode}`.slice(0, 14),
                        full: s.sopName,
                        count: s.count,
                      }))}
                      layout="vertical"
                      margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: number, _n, ctx: any) => [v, ctx?.payload?.full || '']} />
                      <Bar dataKey="count" name={tr('missions_series_missions')} fill="#16a34a" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="inst-missions-card__empty">{tr('missions_chart_empty')}</p>
                )}
              </div>
            </div>

            <div className="inst-missions-card inst-missions-card--wide">
              <h3 className="inst-missions-card__title">{tr('missions_chart_workers')}</h3>
              <div className="inst-missions-card__chart">
                {report.workerActivity.length ? (
                  <ResponsiveContainer width="100%" height={Math.min(420, 48 + report.workerActivity.length * 36)}>
                    <BarChart
                      data={report.workerActivity.slice(0, 12)}
                      layout="vertical"
                      margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="workerName"
                        width={120}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="missionCount" name={tr('missions_series_missions')} stackId="a" fill="#2563eb" />
                      <Bar dataKey="completedMissions" name={tr('missions_series_completed')} stackId="b" fill="#22c55e" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="inst-missions-card__empty">{tr('missions_chart_empty')}</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default InstitutionMissionCharts;
